// "Now playing": one small chip, anywhere in the app, whenever a sound card (src/audio.ts) or the shared video
// (src/player.ts) is playing or paused, with Pause / Play and Stop. 2026-11-04 (Keaton's report): the music bed was
// announced and started during a full build, and nothing on screen could pause it.
import { useEffect } from "react";
import { pauseAudio, resumeAudio, stopAudio } from "../audio";
import { pauseStageNow, resumeStageNow, stopStageNow, trackStage } from "../player";
import { useStore, type NowPlaying as Now } from "../store";
import { Icon } from "./bits";

/** `hideStage`: the full-screen player is up with its own controls, so only a sound card shows here. */
export function NowPlaying({ hideStage = false }: { hideStage?: boolean }) {
  useEffect(() => trackStage(), []);
  const np = useStore((s) => s.nowPlaying);
  const rows: { id: "audio" | "stage"; it: Now; pause: () => void; play: () => void; stop: () => void }[] = [];
  if (np.audio) rows.push({ id: "audio", it: np.audio, pause: pauseAudio, play: resumeAudio, stop: () => stopAudio() });
  if (np.stage && !hideStage) rows.push({ id: "stage", it: np.stage, pause: pauseStageNow, play: resumeStageNow, stop: stopStageNow });
  if (!rows.length) return null;
  return (
    <div className="now-playing" role="region" aria-label="Now playing">
      {rows.map(({ id, it, pause, play, stop }) => (
        <div key={id} className={`now-chip${it.paused ? " is-paused" : ""}`}>
          <span className="now-glyph" aria-hidden>
            <Icon name={id === "audio" ? "music" : "film"} size={14} />
          </span>
          <span className="now-title" title={it.title}>
            <span className="now-kicker">{it.paused ? "Paused" : "Now playing"}</span> {it.title}
          </span>
          <button type="button" className="icon-btn now-btn" onClick={it.paused ? play : pause} aria-label={it.paused ? `Play ${it.title}` : `Pause ${it.title}`}>
            <Icon name={it.paused ? "play" : "pause"} size={14} />
          </button>
          <button data-action="player.close" type="button" className="icon-btn now-btn" onClick={stop} aria-label={`Stop ${it.title}`}>
            <Icon name="stop" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
